import { fmtBRL } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Copy, UserCheck, MessageSquareText, CheckCheck } from "lucide-react";

const SMS = {
  delivered: ["#22C55E", "SMS entregue"],
  sent: ["#3B82F6", "SMS enviado (aguardando confirmação)"],
  failed: ["#EF4444", "SMS não entregue"],
};

const SmsIcon = ({ c }) => {
  const [color, label] = SMS[c.sms_status] || ["#64748B", "SMS ainda não disparado"];
  const when = c.sms_at ? ` · ${new Date(c.sms_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "";
  return (
    <span data-testid={`sms-status-${c.id}`} data-status={c.sms_status || "none"} title={`${label}${when}${c.sms_error ? ` · ${c.sms_error}` : ""}`}
      className="rounded-lg p-2" style={{ background: `${color}22`, color }}>
      <MessageSquareText className="h-4 w-4" />
    </span>
  );
};

const WA = {
  read: ["#38BDF8", "WhatsApp lido"],
  delivered: ["#22C55E", "WhatsApp entregue"],
  sent: ["#3B82F6", "WhatsApp enviado (aguardando entrega)"],
  failed: ["#EF4444", "WhatsApp não entregue"],
};

const WaIcon = ({ c }) => {
  const [color, label] = WA[c.wa_status] || ["#64748B", "WhatsApp ainda não disparado"];
  const d = c.wa_at && (/^\d+$/.test(String(c.wa_at)) ? new Date(Number(c.wa_at) * 1000) : new Date(c.wa_at));
  const when = d && !isNaN(d) ? ` · ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" })}` : "";
  const Icon = c.wa_status === "read" || c.wa_status === "delivered" ? CheckCheck : MessageCircle;
  return (
    <span data-testid={`wa-status-${c.id}`} data-status={c.wa_status || "none"} title={`${label}${when}${c.wa_error ? ` · ${c.wa_error}` : ""}`}
      className="rounded-lg p-2" style={{ background: `${color}22`, color }}>
      <Icon className="h-4 w-4" />
    </span>
  );
};

const waLink = (c) => {
  const first = (c.name || "").split(" ")[0] || "tudo bem";
  const msg = `Olá ${first}! Seu acesso vence hoje.${c.invoice_link ? ` Renove pelo link: ${c.invoice_link}` : ""}`;
  return `https://wa.me/${(c.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;
};

const copy = (c) => navigator.clipboard.writeText(c.invoice_link || "").then(() => toast.success("Link PIX copiado"));

export const DueTodayList = ({ items }) => (
  <div data-testid="list-vencem-hoje" className="lg:col-span-7 rounded-2xl border border-[#1F293D] bg-[#111827] p-5 fade-up" style={{ animationDelay: "200ms" }}>
    <div className="mb-4 flex items-center justify-between">
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200">
        <UserCheck className="h-4 w-4 text-amber-400" /> Vencem hoje
      </h3>
      <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 font-mono text-xs text-amber-300">{items.length}</span>
    </div>
    <div data-testid="sms-legend" className="-mt-2 mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
      <span className="text-slate-400">SMS e WhatsApp:</span>
      {[["#38BDF8", "Lido (WhatsApp)"], ["#22C55E", "Entregue"], ["#3B82F6", "Enviado"], ["#EF4444", "Não entregue"], ["#64748B", "Sem envio"]].map(([c, l]) => (
        <span key={l} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: c }} />{l}</span>
      ))}
    </div>
    {!items.length && <p data-testid="vencem-hoje-empty" className="py-8 text-center text-sm text-slate-500">Nenhum login vence hoje.</p>}
    <div className="max-h-[380px] space-y-1.5 overflow-y-auto pr-1">
      {items.map((c) => (
        <div key={c.id} data-testid={`vence-hoje-${c.id}`} className="group flex items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 transition-colors hover:border-[#1F293D] hover:bg-[#0F141F]">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-slate-100">{c.name || c.panel_user}</div>
            <div className="truncate font-mono text-[11px] text-slate-500">{c.panel_user} · <span className="text-sky-400/80">{c.account}</span> · {c.plan}</div>
          </div>
          <span className="font-mono text-sm font-semibold text-emerald-300">{fmtBRL(c.value)}</span>
          <SmsIcon c={c} />
          <WaIcon c={c} />
          <a data-testid={`btn-wa-${c.id}`} href={c.whatsapp ? waLink(c) : undefined} target="_blank" rel="noreferrer"
            aria-disabled={!c.whatsapp} title={c.whatsapp ? `Abrir conversa no WhatsApp · ${c.whatsapp}` : "Sem WhatsApp"}
            className={`rounded-lg p-2 transition-colors ${c.whatsapp ? "bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20" : "pointer-events-none bg-slate-800/40 text-slate-600"}`}>
            <MessageCircle className="h-4 w-4" />
          </a>
          <button data-testid={`btn-pix-${c.id}`} onClick={() => copy(c)} disabled={!c.invoice_link} title="Copiar link PIX"
            className="rounded-lg bg-sky-500/10 p-2 text-sky-300 transition-colors hover:bg-sky-500/20 disabled:opacity-30">
            <Copy className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  </div>
);
